import React, { useState , memo }  from 'react';
import PropTypes from 'prop-types';
import { FrequentlyBoughtTogether, RelatedProducts } from '@algolia/recommend-react';
import recommend from '@algolia/recommend';
import { InView } from 'react-intersection-observer';

import ProductCardCarousel from '../product-card-carousel';
import ProductCardCarouselWrapper from '../product-algolia-card-carousel';
import approvedBrands from './brands.json';
import { logAmplitude } from '../../utils/amplitude';
import GA4 from '../../utils/ga4DataLayer';

const CarouselSentinel = ({ carouselType, product, children }) => {
  const [wasViewed, setViewed] = useState(false);

  const onCarouselSentinel = (inView) => {
    if (inView && !wasViewed) {
      logAmplitude('Viewed Carousel', {
        positionType: carouselType,
        pageType: 'Product Description Page',
        parentPage: `/product/${product.slug}`,
        productName:product.name,
        productId:product.id,
        brand:product.brand
      });

      // Track GA4 carousel view
      GA4.viewCarousel({
        carousel_name: carouselType,
        carousel_type: 'product',
        item_count: 0, // This would need to be calculated based on actual carousel items
        page_location: `/product/${product.slug}`
      });

      setViewed(true);
    }
  };

  return (
    <InView
      threshold={1}
      onChange={onCarouselSentinel}
    >
      {children}
    </InView>
  );
};

CarouselSentinel.propTypes = {
  carouselType: PropTypes.string.isRequired,
  product: PropTypes.object.isRequired,
  children: PropTypes.node.isRequired
};

const PDPCarousels = ({ product, recentlyViewed, hasRecommendations }) => {
  const recommendClient = recommend(
    process.env.NEXT_PUBLIC_ALGOLIA_APP_ID,
    process.env.NEXT_PUBLIC_ALGOLIA_SEARCH_API_KEY
  );

  return (
    <div>
      {
        approvedBrands[product.brand] && (
          <CarouselSentinel
            carouselType={'more-from-this-brand'}
            product={product}
          >
            <ProductCardCarouselWrapper
              brand={product.brand}
              title="More From This Brand"
              id="more-from-this-brand"
              productName={product.name}
              productID={product.id}
            />
          </CarouselSentinel>
        )
      }

      {
        hasRecommendations
          ? (
            <CarouselSentinel
              carouselType={'related-products'}
              product={product}
            >
              <RelatedProducts
                recommendClient={recommendClient}
                indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                objectIDs={[`${product?.master?.id}`]}
                fallbackComponent={() => (
                  <ProductCardCarouselWrapper
                    brand={product.brand}
                    title="Related Products"
                    id="related-products"
                    productName={product.name}
                    productBreadcrumbs={product.breadcrumb_taxons}
                    productID={product.id}
                  />
                )}
                view={({ items }) => (
                  <ProductCardCarousel
                    title="Related Products"
                    products={items}
                    id="related-products"
                    isInfinite
                  />
                )}
                // eslint-disable-next-line react/no-children-prop
                children={
                        ({ View, Fallback, recommendations }) => (
                          recommendations.length
                            ? <View />
                            : <Fallback />
                        )
                      }
              />
             </CarouselSentinel>
          )
          : null
        }

      {
        (hasRecommendations && !approvedBrands[product.brand])
          ? (
            <CarouselSentinel
              carouselType={'also-bought-with'}
              product={product}
            >
              <FrequentlyBoughtTogether
                recommendClient={recommendClient}
                indexName={process.env.NEXT_PUBLIC_ALGOLIA_PRODUCTS_INDEX}
                objectIDs={[`${product.master?.id}`]}
                // https://www.algolia.com/doc/ui-libraries/recommend/api-reference/recommend-react/FrequentlyBoughtTogether/#param-fallbackcomponent
                fallbackComponent={() => (
                  <ProductCardCarouselWrapper
                    brand={product.brand}
                    title="Also Bought With"
                    id="also-bought-with"
                    productName={product.name}
                    productBreadcrumbs={product.breadcrumb_taxons}
                    productID={product.id}
                  />
                )}
                view={({ items }) => (
                  <ProductCardCarousel
                    title="Also Bought With"
                    products={items}
                    id="also-bought-with"
                    isInfinite
                  />
                )}
                // eslint-disable-next-line react/no-children-prop
                children={
                        ({ View, Fallback, recommendations }) => (
                          recommendations.length
                            ? <View />
                            : <Fallback />
                        )
                      }
              />
            </CarouselSentinel>
          )
          : null
        }

      {
        recentlyViewed.length > 0 && (
          <CarouselSentinel
            carouselType={'recently-viewed'}
            product={product}
          >
            <ProductCardCarousel
              title="Recently Viewed"
              products={recentlyViewed}
              id="recently-viewed"
            />
          </CarouselSentinel>
        )
      }
    </div>
  );
};

PDPCarousels.defaultProps = {
  product: {},
  recentlyViewed: [],
  hasRecommendations: false
};

PDPCarousels.propTypes = {
  product: PropTypes.object,
  recentlyViewed: PropTypes.array,
  hasRecommendations: PropTypes.bool
};

export default memo(PDPCarousels);
